<template>
  <b-overlay :show="loading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container class="py-5 custom-container container">
        <b-breadcrumb
          :items="items"
          class="pl-0 pt-5 mt-5 bg-transparent"
        ></b-breadcrumb>
        <b-row>
          <b-col>
            <h2 class="font-weight-bold">{{ filename }}</h2>
          </b-col>

          <file-options
            v-if="file"
            :file="file"
            :is-requests-file-page="isRequestsFilesPage"
            @showAlert="showAlert"
            @shareFile="shareFile"
          ></file-options>
        </b-row>

        <b-alert
          v-if="loadError"
          show
          variant="danger"
          class="mt-3"
          data-testid="requests-file-view-load-error"
        >
          {{ loadError }}
        </b-alert>

        <!-- <b-row v-if="file" class="mx-0 my-2">
          <b-card no-body class="files">
            <b-img-lazy
              v-bind="mainProps"
              class="image"
              :src="fileSrc"
            ></b-img-lazy>
          </b-card>
        </b-row> -->
        <b-row class="mx-0 mt-4">
          <b-col sm="12" lg="8" md="8" class="d-flex flex-column"
            ><b-row class="preview-container" v-if="file">
              <b-col
                sm="6"
                lg="5"
                class="pl-0"
                v-for="image in file.files"
                :key="image.id"
              >
                <b-card no-body class="files"
                  ><b-img-lazy
                    v-bind="mainProps"
                    class="image"
                    :src="image.thumb_url"
                  ></b-img-lazy></b-card
              ></b-col>
            </b-row>
            <b-row class="mt-auto" v-if="file">
              <b-col cols="12" class="mb-0">
                <b-card class="mt-4 file-details">
                  <h5 class="mb-4 font-weight-bold">File Details</h5>
                  <b-card-body class="pl-0 align-items-center">
                    <b-row v-if="attributionSamePerson" class="mb-2">
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        Created &amp; owned by
                      </b-col>
                      <b-col cols="5" sm="5" md="4" lg="4" class="gray--2">{{
                        ownedByLabel
                      }}</b-col>
                    </b-row>
                    <template v-else>
                      <b-row class="mb-2">
                        <b-col
                          cols="7"
                          sm="7"
                          md="4"
                          lg="4"
                          class="font-weight-bold gray--2"
                        >
                          Created by
                        </b-col>
                        <b-col cols="5" sm="5" md="4" lg="4" class="gray--2">{{
                          createdByLabel
                        }}</b-col>
                      </b-row>
                      <b-row class="mb-2">
                        <b-col
                          cols="7"
                          sm="7"
                          md="4"
                          lg="4"
                          class="font-weight-bold gray--2"
                        >
                          Owned by
                        </b-col>
                        <b-col cols="5" sm="5" md="4" lg="4" class="gray--2">{{
                          ownedByLabel
                        }}</b-col>
                      </b-row>
                    </template>
                    <b-row
                      class="mb-2"
                      v-for="info in visibleInfoTypes"
                      :key="info.id"
                    >
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        {{ info.label }}
                      </b-col>
                      <b-col cols="5" sm="5" md="4" lg="4" class="gray--2">
                        {{
                          info.field_type === 'date'
                            ? parseDate(info.value)
                            : info.value
                        }}
                      </b-col>
                    </b-row>
                  </b-card-body>
                  <div class="file-submit-actions">
                    <b-button
                      class="file-submit-actions__btn"
                      variant="outline-secondary"
                      :disabled="loading"
                      @click="
                        $router.replace({
                          name: 'request.files-page',
                          query: { name: $route.query.name },
                        })
                      "
                    >
                      Back
                    </b-button>
                    <b-button
                      type="submit"
                      class="file-submit-actions__btn"
                      variant="primary"
                      :disabled="loading"
                      @click="submitFile"
                    >
                      <b-spinner v-if="loading" small label="Loading" />
                      <span v-else>Submit</span>
                    </b-button>
                  </div>
                </b-card>
              </b-col>
            </b-row>
          </b-col>

          <b-col v-if="file" sm="12" lg="4" md="4" class="upload-details"
            ><file-upload-details
              :file="file"
              @openShareFile="shareFile"
            ></file-upload-details>
          </b-col>
        </b-row>

        <share-file-modal
          :showShareFiles="showShareFiles"
          :selectedFiles="checkedFiles"
          :fileShares="fileShares"
          @closeShareFileModal="showShareFiles = false"
          @fileShareResponsePrompt="shareFileResponsePrompt"
          @reloadFileList="reloadFileShareState"
        />

        <b-alert
          class="custom-alert"
          :show="dismissCountDown"
          :class="{
            'bg-danger border-danger': alertType === 'danger',
          }"
          dismissible
          fade
          :variant="alertType"
          @dismiss-count-down="countDownChanged"
        >
          {{ alertMsg }}
        </b-alert>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import FileOptions from '@/components/modals/FileOptions'
import FileUploadDetails from '@/components/cards/FileUploadDetails'
import ShareFileModal from '@/components/modals/ShareFileModal'
import {
  getCreatedByLabel,
  getOwnedByLabel,
  isSameCreatorAndOwner,
  visibleFileInformationTypes,
} from '@/utils/fileLabels'

export default {
  components: {
    FileOptions,
    FileUploadDetails,
    ShareFileModal,
  },
  data() {
    return {
      loading: false,
      loadError: '',
      mainProps: {
        center: true,
        blank: true,
        blankColor: '#bbb',
      },
      isRequestsFilesPage: {
        type: Boolean,
        default: false,
      },

      showShareFiles: false,
      checkedFiles: [],
      fileShares: [],
      dismissCountDown: 0,
      alertMsg: '',
      alertType: 'primary',
    }
  },

  computed: {
    ...mapState({
      file: (state) => state.file.file,
    }),
    ...mapGetters('file', ['file']),
    ...mapGetters('auth', ['user']),
    ...mapGetters('requests', ['fileRequest']),
    // Drops the retired "Consent" information type (see fileLabels).
    visibleInfoTypes() {
      return visibleFileInformationTypes(
        this.file && this.file.file_information_types,
      )
    },
    filename() {
      return this.file ? this.file.name : ''
    },
    items() {
      return [
        {
          text: 'Files',
          to: '/file',
        },
        {
          text: this.filename,
        },
      ]
    },
    fileSrc() {
      return this.file
        ? this.file.files.length
          ? this.file.files[0].thumb_url
          : `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%230066FF' class='bi bi-file-earmark' viewBox='0 0 16 16'%3E%3Cpath d='M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z'/%3E%3C/svg%3E`
        : `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%230066FF' class='bi bi-file-earmark' viewBox='0 0 16 16'%3E%3Cpath d='M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z'/%3E%3C/svg%3E`
    },
    ownedByLabel() {
      return this.file ? getOwnedByLabel(this.file) : ''
    },
    createdByLabel() {
      return this.file
        ? getCreatedByLabel(this.file, (id) => this.resolveCreatorName(id))
        : ''
    },
    attributionSamePerson() {
      return this.file
        ? isSameCreatorAndOwner(this.file, (id) => this.resolveCreatorName(id))
        : false
    },
  },

  methods: {
    ...mapActions({
      getFile: 'file/getFile',
      fileRequestShare: 'requests/fileRequestShare',
    }),

    resolveCreatorName(id) {
      if (this.user && this.user.id === id) {
        return (this.user.full_name || this.user.email || '').trim() || null
      }
      const u =
        (this.file && this.file.user) ||
        (this.file && this.file.file && this.file.file.user)
      if (u && Number(u.id) === Number(id)) {
        return (u.full_name || u.email || '').trim() || null
      }
      return null
    },

    async fetchFile() {
      this.loading = true
      this.loadError = ''
      try {
        await this.getFile(this.$route.params.id)
      } catch (err) {
        // Clear any previously viewed file so it doesn't keep rendering
        // behind the error banner when navigating file -> file.
        this.$store.commit('file/clearFile')
        const status = err && err.response && err.response.status
        if (status === 403) {
          this.loadError = "You don't have access to this file."
        } else if (status === 404) {
          this.loadError = 'This file could not be found.'
        } else {
          this.loadError = 'Could not load this file.'
        }
      } finally {
        this.loading = false
      }
    },
    shareFile() {
      this.fileShares = this.file.file_shares
      this.showShareFiles = true
    },
    // Revoking a share (the modal's "x" on a "Currently shared with" tag)
    // doesn't close the modal, so the modal's fileShares prop must be
    // refreshed here too, or reopening it later still shows the revoked email.
    async reloadFileShareState() {
      await this.fetchFile()
      this.fileShares = this.file.file_shares || []
    },
    shareFileResponsePrompt(res) {
      if (res.success) {
        this.alertMsg =
          'File request shared successfully to selected emails: ' +
          res.data.success.join(', ')

        this.dismissCountDown = 15
        this.fetchFile()

        return
      } else {
        this.alertType = 'danger'

        if (res.status === 402) {
          this.alertMsg = 'File shared unsuccessful!'
        } else {
          this.alertMsg =
            'Something went wrong while submitting the share file email!'
        }
      }

      this.dismissCountDown = 5
    },
    // A private file only grants the requesting company access via the
    // FileShare this submit creates - it never makes the file itself
    // visible to them in any other context. Some companies still expect to
    // just open a file from the request like anything else, so ask upfront
    // whether to make it Public rather than send a private file's ID and
    // have it be confusing. isRequestFileCreation-created files already
    // force Public (see FileForm.vue) - this is the "answer with an
    // existing, already-private file" path that never got that treatment.
    async confirmMakePublicIfPrivate() {
      if (
        !this.file ||
        String(this.file.visibility).toLowerCase() !== 'private'
      ) {
        return true
      }

      const ok = await this.$bvModal.msgBoxConfirm(
        'This file is private. To respond to this request, it needs to be made Public so the company that requested it can see it.',
        {
          title: 'Make this file public?',
          okTitle: 'Yes, make it public',
          cancelTitle: 'Cancel',
          okVariant: 'primary',
          centered: true,
          modalClass: 'confirm-modal',
        },
      )
      if (!ok) return false

      try {
        await this.$api.post(`file/${this.file.id}`, {
          name: this.file.name,
          information_types: (this.file.file_information_types || []).map(
            (info) => ({
              information_type_id: info.information_type_id,
              value: info.value,
            }),
          ),
          visibility: 'public',
          // Tells the API this public flip is answering this file request,
          // so it skips the separate "made file public" notification - the
          // fileRequestShare() call below already notifies the requester.
          file_request_id: this.fileRequest,
        })
        await this.getFile(this.file.id)
        return true
      } catch (error) {
        this.alertType = 'danger'
        this.dismissCountDown = 15
        this.alertMsg =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Could not make the file public. Please try again.'
        return false
      }
    },

    async submitFile() {
      const canProceed = await this.confirmMakePublicIfPrivate()
      if (!canProceed) return

      let formData = {
        fileRequestId: this.fileRequest,
        file: this.file.id,
      }

      await this.fileRequestShare(formData)
        .then((res) => {
          this.$router.replace({
            name: 'file.success-page',
            params: {
              id: this.file.id,
            },
          })
        })
        .catch((error) => {
          this.alertType = 'danger'
          this.dismissCountDown = 15
          this.alertMsg = error.data.message
        })
    },
    showAlert(action) {
      this.dismissCountDown = 1
      // this.alertMsg = action
      this.alertMsg = `File has been ${{ action }}.`
      if (action === 'deleted') {
        this.$router.replace({
          name: 'file.list-page',
        })
      }
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
  },

  async created() {
    if (this.fileRequest === null) {
      this.$router
        .replace({
          name: 'requests.list-page',
        })
        .catch(() => {})
    }

    await this.fetchFile().then(() => {
      if (this.file) {
        this.checkedFiles.push(this.file.id)
      }
    })
  },
}
</script>
<style lang="scss" scoped>
.file-details {
  height: auto;
}

.file-submit-actions {
  margin: 1.5rem -1.25rem -1.25rem;
  padding: 1.25rem;
  border-top: 1px solid #eceff3;
  background: #fafbfc;
  border-radius: 0 0 8px 8px;
  display: flex;
  align-items: stretch;
  gap: 1rem;

  &__btn {
    flex: 1 1 0;
    height: 56px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 !important;
  }
}

@media (max-width: 767px) {
  .file-submit-actions {
    flex-direction: column;
  }
}

.card {
  border-radius: 8px;
  opacity: 1;
  margin: 0;
}

.preview {
  width: 1440px;
  height: 808px;

  @media (max-width: 767px) {
    display: none;
  }
}

.preview-container {
  height: 192px;
  flex-wrap: nowrap;

  @media (max-width: 767px) {
    width: auto;
  }
}

.files {
  height: 192px;
}

.image {
  margin: 0 !important;
  width: 100%;
  height: 100%;
  // Match the normal file-view page: letterbox the thumbnail in its box
  // instead of stretching a portrait image to the card width.
  object-fit: contain;
}

.list {
  justify-content: space-around;
}

.upload-details {
  padding-right: 0;

  @media (max-width: 767px) {
    padding-left: 0;
    padding-top: 1.5rem;
  }
}
</style>
